<template>
  <v-card class="pa-4">
    <h2>Compliance Ratio (By Branch & Department)</h2>
    <v-row dense>
      <v-col cols="12" md="3">
        <v-autocomplete
          v-model="selectedDept"
          :items="departments"
          item-text="name"
          item-value="id"
          label="Department"
          dense
          required
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-autocomplete
          v-model="selectedClassification"
          :items="classificationOptions"
          item-text="subCode"
          item-value="id"
          label="File Classification"
          dense
          :disabled="!selectedDept"
          clearable
          multiple
          chips
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-menu v-model="menuMonth" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="selectedMonth"
              label="Month"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              dense
              required
            />
          </template>
          <v-date-picker
            v-model="selectedMonth"
            type="month"
            @change="menuMonth = false"
          />
        </v-menu>
      </v-col>
      <v-col cols="12" md="3" class="d-flex align-end">
        <v-btn
          color="primary"
          @click="generateReport"
          :disabled="!selectedDept || !selectedMonth"
        >
          Generate Report
        </v-btn>
      </v-col>
    </v-row>
    <v-divider class="my-4" />
    <div v-if="tableRows.length">
      <!-- Title and subtitle rows -->
      <div class="mb-2">
        <div
          style="
            background: #92d050;
            font-family: Tahoma;
            font-size: 1.1rem;
            font-weight: bold;
            text-align: center;
            padding: 8px;
            border-radius: 4px;
          "
        >
          COMPLIANCE SUMMARY {{ monthTitle }}
        </div>
        <div
          style="
            background: #ededed;
            font-family: Tahoma;
            font-size: 0.95rem;
            text-align: center;
            padding: 6px;
            border-radius: 4px;
          "
        >
          Assignment Ratio by Branch,
          <span style="font-weight: bold">
            {{
              selectedDept === "all" || !selectedDept
                ? "All Departments"
                : departments.find((d) => d.id === selectedDept)?.name ||
                  "Department"
            }}
          </span>
          ,
          <span style="font-weight: bold">
            {{
              !selectedClassification ||
              (Array.isArray(selectedClassification) &&
                (selectedClassification.length === 0 ||
                  selectedClassification.includes("all")))
                ? "All Classifications"
                : classificationOptions
                    .filter((c) => selectedClassification.includes(c.id))
                    .map((c) => c.subCode)
                    .join(", ") || "Classification"
            }}
          </span>
        </div>
        <div
          style="
            background: #fffbe6;
            color: #856404;
            font-family: Tahoma;
            font-size: 0.95rem;
            text-align: center;
            padding: 6px;
            border-radius: 4px;
            margin-top: 6px;
          "
        >
          For detailed breakdown by department and classification, please click
          <strong>Export to Excel</strong>.
        </div>
        <!-- OVERALL RATIO -->
        <div
          style="
            background: #e6f7ff;
            color: #00529b;
            font-family: Tahoma;
            font-size: 1.05rem;
            text-align: center;
            padding: 8px;
            border-radius: 4px;
            margin-top: 10px;
            font-weight: bold;
          "
        >
          OVERALL RATIO:
          {{
            totals.total > 0
              ? ((totals.complied / totals.total) * 100).toFixed(2)
              : "0.00"
          }}%
          <span v-if="totals.total > 0">
            - ({{ totals.complied }}/{{ totals.notComplied }}/{{
              totals.total
            }})
          </span>
        </div>
      </div>
      <v-btn
        color="success"
        class="mb-4"
        @click="exportExcel"
        :loading="exporting"
      >
        <v-icon left>mdi-file-excel</v-icon>
        Export to Excel
      </v-btn>
      <v-simple-table>
        <thead>
          <tr style="background: #ededed">
            <th
              v-for="header in tableHeaders"
              :key="header"
              style="
                font-family: Tahoma;
                font-size: 13px;
                font-weight: bold;
                text-transform: uppercase;
                text-align: center;
                border: 1px solid #bbb;
              "
            >
              {{ header }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, idx) in tableRows"
            :key="idx"
            :style="
              row.isSummary
                ? 'font-family:Tahoma;font-size:13px;font-weight:bold;background:#fff;border:1px solid #bbb;'
                : 'font-family:Tahoma;font-size:13px;background:#fff;border:1px solid #bbb;'
            "
          >
            <td
              :style="
                row.isSummary
                  ? 'text-align:left;font-weight:bold;'
                  : 'text-align:left;'
              "
            >
              {{ row.branch }}
            </td>
            <td style="text-align: center; font-weight: bold">
              {{ row.complied }}
            </td>
            <td style="text-align: center; font-weight: bold; color: #f00">
              {{ row.notComplied }}
            </td>
            <td style="text-align: center; font-weight: bold">
              {{ row.total }}
            </td>
            <td style="text-align: center; font-weight: bold">
              {{ row.percent }}
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>
    <v-alert v-else-if="noDataMessage" type="warning" outlined class="mt-4">
      {{ noDataMessage }}
    </v-alert>
    <v-alert v-else type="info" outlined class="mt-4">
      Please select filters and click "Generate Report" to view data.
    </v-alert>
  </v-card>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { apiConfig } from "../../config/api.config";

export default {
  name: "ComplianceRatioBranch",
  data() {
    return {
      selectedDept: null,
      selectedClassification: [],
      selectedMonth: null,
      menuMonth: false,
      departments: [],
      classificationOptions: [],
      reportData: [],
      totals: {
        complied: 0,
        notComplied: 0,
        total: 0,
      },
      exporting: false,
      noDataMessage: "",
    };
  },
  watch: {
    selectedDept(val) {
      this.loadClassifications(val);
      this.selectedClassification = [];
    },
    selectedClassification: {
      handler(val, oldVal) {
        const allOption = "all";
        const allIds = this.classificationOptions
          .filter((opt) => opt.id !== allOption)
          .map((opt) => opt.id);

        if (Array.isArray(val)) {
          if (
            val.includes(allOption) &&
            (!oldVal || !oldVal.includes(allOption))
          ) {
            this.selectedClassification = [allOption, ...allIds];
          } else if (
            oldVal &&
            oldVal.includes(allOption) &&
            !val.includes(allOption)
          ) {
            this.selectedClassification = [];
          } else {
            const selectedWithoutAll = val.filter((v) => v !== allOption);
            if (
              selectedWithoutAll.length === allIds.length &&
              !val.includes(allOption)
            ) {
              this.selectedClassification = [allOption, ...allIds];
            } else if (
              val.includes(allOption) &&
              selectedWithoutAll.length !== allIds.length
            ) {
              this.selectedClassification = selectedWithoutAll;
            }
          }
        }
      },
      deep: true,
    },
  },
  computed: {
    monthTitle() {
      if (!this.selectedMonth) return "";
      const m = this.selectedMonth;
      const dt = typeof m === "string" ? m : m.format("YYYY-MM");
      const dObj = new Date(dt + "-01");
      const monthName = dObj
        .toLocaleString("default", { month: "long" })
        .toUpperCase();
      const year = dObj.getFullYear();
      return `${monthName} ${year}`;
    },
    tableHeaders() {
      return ["Branch", "Complied", "Not Complied", "Total", "Complied (%)"];
    },
    tableRows() {
      // Sort summary rows and detail rows by branch, then dept, then classification (already sorted by backend)
      if (!Array.isArray(this.reportData) || this.reportData.length === 0)
        return [];
      return this.reportData.map((row) => ({
        branch: row.branch,
        complied: row.complied,
        notComplied: row.notComplied,
        total: row.total,
        percent: row.percent,
        isSummary: row.isSummary,
      }));
    },
  },
  methods: {
    async loadDepartments() {
      const res = await axios.get(`${apiConfig.baseURL}/depts`);
      this.departments = [
        { id: "all", name: "All Departments" },
        ...res.data.sort((a, b) => a.name.localeCompare(b.name)),
      ];
    },
    async loadClassifications(deptId) {
      if (!deptId || deptId === "all") {
        this.classificationOptions = [
          { id: "all", subCode: "Select/Deselect All" },
        ];
        this.selectedClassification = [];
        return;
      }
      const res = await axios.get(
        `${apiConfig.baseURL}/subDepts?deptId=${deptId}`
      );
      this.classificationOptions = [
        { id: "all", subCode: "Select/Deselect All" },
        ...res.data
          .filter((c) => c.deptId === deptId)
          .sort((a, b) => a.subCode.localeCompare(b.subCode)),
      ];
      this.selectedClassification = [];
    },
    async generateReport() {
      this.reportData = [];
      this.totals = { complied: 0, notComplied: 0, total: 0 };
      this.noDataMessage = "";
      try {
        let classificationIds = this.selectedClassification;
        if (
          Array.isArray(classificationIds) &&
          classificationIds.includes("all")
        ) {
          classificationIds = classificationIds.filter((id) => id !== "all");
        }
        if (!classificationIds || classificationIds.length === 0) {
          classificationIds = undefined;
        }
        const params = {
          month: moment(this.selectedMonth).format("YYYY-MM"),
          deptId: this.selectedDept,
          ...(classificationIds && { classificationIds }),
        };
        const res = await axios.get(
          `${apiConfig.baseURL}/compliance/ratio-branch`,
          { params }
        );
        this.processReportData(res.data);
        if (
          !Array.isArray(res.data.reportData) ||
          res.data.reportData.length === 0
        ) {
          const deptName =
            this.departments.find((d) => d.id === this.selectedDept)?.name ||
            "selected department";
          let className;
          if (
            !this.selectedClassification ||
            (Array.isArray(this.selectedClassification) &&
              (this.selectedClassification.length === 0 ||
                this.selectedClassification.includes("all")))
          ) {
            className = "all classifications";
          } else {
            className =
              this.classificationOptions
                .filter((c) => this.selectedClassification.includes(c.id))
                .map((c) => c.subCode)
                .join(", ") || "selected classification";
          }
          const monthStr = moment(this.selectedMonth).format("MMMM YYYY");
          this.noDataMessage = `No compliance data found for ${deptName} and ${className} in ${monthStr}.`;
        }
      } catch (err) {
        this.noDataMessage =
          err.message || "Failed to fetch report data. Please try again.";
      }
    },
    processReportData(data) {
      this.reportData = Array.isArray(data.reportData) ? data.reportData : [];
      // Only sum summary rows for totals
      const totals = { complied: 0, notComplied: 0, total: 0 };
      this.reportData.forEach((row) => {
        if (row.isSummary) {
          totals.complied += row.complied;
          totals.notComplied += row.notComplied;
          totals.total += row.total;
        }
      });
      this.totals = totals;
    },
    async exportExcel() {
      this.exporting = true;
      try {
        let classificationIds = this.selectedClassification;
        if (
          Array.isArray(classificationIds) &&
          classificationIds.includes("all")
        ) {
          classificationIds = classificationIds.filter((id) => id !== "all");
        }
        if (!classificationIds || classificationIds.length === 0) {
          classificationIds = undefined;
        }
        const params = {
          month: moment(this.selectedMonth).format("YYYY-MM"),
          deptId: this.selectedDept,
          ...(classificationIds && { classificationIds }),
        };
        const url =
          `${apiConfig.baseURL}/compliance/ratio-branch/export?` +
          new URLSearchParams(params).toString();
        window.open(url, "_blank");
      } catch (err) {
        this.$emit("error", err.message || "Failed to export Excel");
      } finally {
        this.exporting = false;
      }
    },
  },
  mounted() {
    this.loadDepartments();
  },
};
</script>
